import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../../../apiClient';
import { translate } from '../../../i18n';
import { PAYMENT_INTENTS_FOR_COACHING_SESSION_QUERY_KEY } from './useGetPaymentIntentsForCoachingSession';

interface ChargePayLaterResponse {
  success: boolean;
  paymentIntentId: string;
  originalPaymentIntentId: string;
  status: string;
  amount: number;
  currency: string;
  message: string;
  errorCode?: string;
  requiresAuthentication?: boolean;
  clientSecret?: string;
}

interface ChargePayLaterRequest {
  paymentMethodId?: string;
  force?: boolean;
  customAmount?: number;
}

export const useChargeCoachingPayLater = (coachingSessionId: string) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const apiClient = useApiClient();

  return useMutation({
    mutationFn: async ({
      paymentIntentId,
      options = {},
    }: {
      paymentIntentId: string;
      options?: ChargePayLaterRequest;
    }) => {
      return apiClient.fetchApi<ChargePayLaterResponse, ChargePayLaterRequest>({
        endpoint: `payments/charge-pay-later-booking/${paymentIntentId}`,
        method: 'POST',
        body: options,
      });
    },
    onSuccess: (data) => {
      if (data.success) {
        toast({
          title: translate('game.payment_charged_successfully'),
          description: data.message,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        queryClient.invalidateQueries({
          queryKey: PAYMENT_INTENTS_FOR_COACHING_SESSION_QUERY_KEY(coachingSessionId),
        });
      } else {
        toast({
          title: translate('game.payment_failed'),
          description: data.requiresAuthentication
            ? 'Le joueur doit authentifier sa carte (3DS). Demandez-lui de se reconnecter.'
            : data.message,
          status: 'error',
          duration: 8000,
          isClosable: true,
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: translate('game.error_charging_payment'),
        description: error?.message || translate('common.unexpected_error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });
};
